import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, AreaChart, Area } from "recharts";
import { TrendingUp, Percent, Info, Receipt, Layers, ArrowDownUp, PieChart as PieIcon, BarChart3, LineChart } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";

const TRIBUTOS_CONFIG = [
  { sigla: "ICMS",    label: "ICMS",     cor: "#FF7A00", desc: "Imposto sobre Circulação de Mercadorias e Serviços" },
  { sigla: "ICMS-ST", label: "ICMS-ST",  cor: "#FF9A3D", desc: "Substituição Tributária do ICMS" },
  { sigla: "FCP",     label: "FCP",      cor: "#6366F1", desc: "Fundo de Combate à Pobreza" },
  { sigla: "FCP-ST",  label: "FCP-ST",   cor: "#818CF8", desc: "FCP na Substituição Tributária" },
  { sigla: "IPI",     label: "IPI",      cor: "#10B981", desc: "Imposto sobre Produtos Industrializados" },
  { sigla: "PIS",     label: "PIS",      cor: "#F59E0B", desc: "Programa de Integração Social" },
  { sigla: "COFINS",  label: "COFINS",   cor: "#EC4899", desc: "Contribuição para o Financiamento da Seguridade Social" },
  { sigla: "IBS",     label: "IBS",      cor: "#0EA5E9", desc: "Imposto sobre Bens e Serviços (Reforma Tributária)" },
  { sigla: "CBS",     label: "CBS",      cor: "#14B8A6", desc: "Contribuição sobre Bens e Serviços (Reforma Tributária)" },
  { sigla: "ISSQN",   label: "ISSQN",    cor: "#F97316", desc: "Imposto Sobre Serviços de Qualquer Natureza" },
];

type SortKey = "sigla" | "base" | "valor" | "aliq";

export default function TributacaoPage() {
  const { filteredNotas, dashboard } = useFiscalStore();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [sortKey, setSortKey] = useState<SortKey>("valor");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => { document.title = "Tax Analytics — DataGrid XML"; }, []);

  const { consolidadoE, consolidadoS, historicoST, historicoSimples } = useMemo(() => {
    const accE: Record<string, { base: number; valor: number; count: number }> = {};
    const accS: Record<string, { base: number; valor: number; count: number }> = {};
    const mapST = new Map<string, { mes: string; vendas: number; vendasST: number }>();
    const mapSN = new Map<string, { mes: string; entradasSN: number; devolucoes: number; cancelamentos: number }>();

    const topCnpj = dashboard?.cnpjEmpresa?.replace(/\D/g, "");

    for (const nota of filteredNotas) {
      if (nota.emissao) {
        const date = new Date(nota.emissao);
        if (!isNaN(date.getTime())) {
          const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          let op = nota.tipoOperacao;
          if (op === "INDEFINIDA" && topCnpj) {
            if (nota.cnpjDest.replace(/\D/g, "") === topCnpj) op = "ENTRADA";
            else if (nota.cnpjEmit.replace(/\D/g, "") === topCnpj) op = "SAIDA";
          }

          const isEntrada = op === "ENTRADA";
          const isSaida = op === "SAIDA";
          
          if (!mapST.has(monthYear)) mapST.set(monthYear, { mes: monthYear, vendas: 0, vendasST: 0 });
          if (isSaida && nota.notaStatus !== "CANCELADA") {
            mapST.get(monthYear)!.vendas += nota.valorOficial;
            if (nota.vST > 0) mapST.get(monthYear)!.vendasST += nota.valorOficial;
          }

          if (!mapSN.has(monthYear)) mapSN.set(monthYear, { mes: monthYear, entradasSN: 0, devolucoes: 0, cancelamentos: 0 });
          if (isEntrada) {
            const crt = nota.detalhes?.emitente?.CRT;
            if (nota.notaStatus === "CANCELADA") mapSN.get(monthYear)!.cancelamentos += nota.valorOficial;
            else if (nota.cfopPrincipal?.includes("201") || nota.cfopPrincipal?.includes("410")) mapSN.get(monthYear)!.devolucoes += nota.valorOficial;
            else if (crt === "1") mapSN.get(monthYear)!.entradasSN += nota.valorOficial;
          }
        }
      }
    }

    return {
      consolidadoE: accE,
      consolidadoS: accS,
      historicoST: Array.from(mapST.values()).sort((a, b) => a.mes.localeCompare(b.mes)),
      historicoSimples: Array.from(mapSN.values()).sort((a, b) => a.mes.localeCompare(b.mes)).map(h => ({
         ...h, 
         resultado: h.entradasSN - h.devolucoes - h.cancelamentos
      }))
    };

  }, [filteredNotas, dashboard]);

  const totalReceitaE = dashboard?.operacoesSummary?.entradas.valorTotal || 0;
  const totalReceitaS = dashboard?.operacoesSummary?.saidas.valorTotal || 0;
  
  const consolidadoTotal = useMemo(() => {
    const acc: Record<string, { base: number; valor: number; count: number }> = {};
    [consolidadoE, consolidadoS].forEach(c => {
      Object.entries(c).forEach(([k, v]) => {
        if (!acc[k]) acc[k] = { base: 0, valor: 0, count: 0 };
        acc[k].base += v.base;
        acc[k].valor += v.valor;
        acc[k].count += v.count;
      });
    });
    return acc;
  }, [consolidadoE, consolidadoS]);

  const totalTributosGeral = Object.values(consolidadoTotal).reduce((s, v) => s + v.valor, 0);
  const totalBaseGeral = Object.values(consolidadoTotal).reduce((s, v) => s + v.base, 0);

  const tableData = useMemo(() => {
    return TRIBUTOS_CONFIG
      .map(t => {
        const dados = consolidadoTotal[t.sigla] || { base: 0, valor: 0 };
        const aliq = dados.base > 0 ? (dados.valor / dados.base) * 100 : 0;
        const pct = totalTributosGeral > 0 ? (dados.valor / totalTributosGeral) * 100 : 0;
        return { ...t, ...dados, aliq, pct };
      })
      .filter(t => t.valor > 0)
      .sort((a, b) => {
        const factor = sortDir === "asc" ? 1 : -1;
        if (sortKey === "sigla") return a.sigla.localeCompare(b.sigla) * factor;
        return (a[sortKey] - b[sortKey]) * factor;
      });
  }, [consolidadoTotal, sortKey, sortDir, totalTributosGeral]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-4 animate-in fade-in duration-700">
      {/* Header Bento Section */}
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-px w-8 bg-primary" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">Compliance Hub</span>
          </div>
          <h1 className="text-4xl font-black tracking-tighter">
            Análise <span className="text-primary">Tributária</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 font-medium max-w-xl">
            Cálculo de carga tributária, alíquotas efetivas e distribuição técnica baseada no engine de processamento.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 glass border-primary/10 rounded-xl">
          <Receipt className="size-4 text-primary" />
          <span className="text-sm font-black text-foreground">{fmtNum(Object.keys(consolidadoTotal).length)} Tributos Identificados</span>
        </div>
      </header>

      {/* Tax Analysis Sections */}
      <TaxAnalysisSection 
        title="Impostos de Entrada" 
        subtitle="Créditos e Carga Tributária (Inbound)"
        consolidado={consolidadoE} 
        totalReceita={totalReceitaE}
        icon={ArrowDownUp}
        color="emerald"
      />

      <TaxAnalysisSection 
        title="Impostos de Saída" 
        subtitle="Débitos e Carga Tributária (Outbound)"
        consolidado={consolidadoS} 
        totalReceita={totalReceitaS}
        icon={Layers}
        color="primary"
      />

      {/* HISTÓRICOS INSPIRADOS EM DATA SCIENCE E TRIBUTÁRIO */}
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        
        {/* Saídas com Substituição Tributária */}
        <div className="glass-card p-6 flex flex-col gap-4 relative overflow-hidden group hover-glow">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[80px] pointer-events-none transition-all duration-700 group-hover:opacity-100 opacity-50" />
          
          <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 mb-2 relative z-10 text-muted-foreground">
            <LineChart className="size-4 text-primary" /> Tendência de Substituição Tributária (ST)
          </h2>
          
          <div className="flex flex-col xl:flex-row gap-6 items-center">
            <div className="xl:w-2/3 w-full h-[220px] relative z-10">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historicoST} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                  <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700 }} />
                  <Tooltip 
                    formatter={(val: number) => fmtBRL(val)}
                    contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px', fontWeight: 'bold', textTransform: 'uppercase' }} />
                  <Area type="monotone" dataKey="vendas" name="Vendas Brutas" stroke="#10B981" fillOpacity={0.1} fill="#10B981" strokeWidth={3} />
                  <Area type="monotone" dataKey="vendasST" name="Vendas com ST" stroke="#FF7A00" strokeWidth={3} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="xl:w-1/3 w-full space-y-4">
              <div className="glass border-primary/5 p-5 rounded-2xl flex flex-col gap-4">
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  Total de Vendas <span className="text-emerald-500 text-xs">{fmtBRL(historicoST.reduce((s,i) => s + i.vendas, 0))}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-foreground/5 pb-4">
                  Vendas com ST <span className="text-primary text-xs">{fmtBRL(historicoST.reduce((s,i) => s + i.vendasST, 0))}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest pt-1 text-foreground">
                  Índice de ST 
                  <span className="bg-primary/10 text-primary px-3 py-1 rounded-lg">
                    {historicoST.reduce((s,i) => s + i.vendas, 0) > 0 ? ((historicoST.reduce((s,i) => s + i.vendasST, 0) / historicoST.reduce((s,i) => s + i.vendas, 0)) * 100).toFixed(0) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico de Entradas - Simples Nacional */}
        <div className="glass-card p-6 flex flex-col gap-4 relative overflow-hidden group hover-glow">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none transition-all duration-700 group-hover:opacity-100 opacity-50" />
          
          <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 mb-2 relative z-10 text-muted-foreground">
            <BarChart3 className="size-4 text-emerald-500" /> Fluxo Simples Nacional
          </h2>
          
          <div className="flex flex-col xl:flex-row gap-6 items-center">
            <div className="xl:w-2/3 w-full h-[220px] relative z-10">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historicoSimples} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                  <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700 }} />
                  <Tooltip 
                    formatter={(val: number) => fmtBRL(val)}
                    contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px', fontWeight: 'bold', textTransform: 'uppercase' }} />
                  <Area type="monotone" dataKey="entradasSN" name="Entradas (SN)" stroke="#3B82F6" strokeWidth={3} fillOpacity={0.1} fill="#3B82F6" />
                  <Area type="monotone" dataKey="devolucoes" name="Devoluções" stroke="#F59E0B" strokeWidth={3} fillOpacity={0.1} fill="#F59E0B" />
                  <Area type="monotone" dataKey="cancelamentos" name="Cancelamentos" stroke="#EF4444" strokeWidth={3} fillOpacity={0.1} fill="#EF4444" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="xl:w-1/3 w-full space-y-4">
              <div className="glass border-primary/5 p-5 rounded-2xl flex flex-col gap-4">
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  Entradas (SN) <span className="text-blue-500 text-xs">{fmtBRL(historicoSimples.reduce((s,i) => s + i.entradasSN, 0))}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  Devoluções <span className="text-amber-500 text-xs">{fmtBRL(historicoSimples.reduce((s,i) => s + i.devolucoes, 0))}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-foreground/5 pb-4">
                  Canceladas <span className="text-red-500 text-xs">{fmtBRL(historicoSimples.reduce((s,i) => s + i.cancelamentos, 0))}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest pt-1 text-foreground">
                  Resultado <span className="text-emerald-500 text-xs">{fmtBRL(historicoSimples.reduce((s,i) => s + i.resultado, 0))}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Detalhamento com Inteligência */}
      <div className="glass-card overflow-hidden">
        <div className="p-6 border-b border-foreground/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
              <Layers className="size-6" />
            </div>
            <div>
              <h3 className="text-xl font-black tracking-tight">Matriz de Impostos</h3>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mt-1">Detalhamento Técnico por Tributo</p>
            </div>
          </div>
          <div className="group relative">
            <Info className="size-5 text-muted-foreground cursor-help" />
            <div className="absolute right-0 bottom-full mb-2 w-72 p-4 glass-card bg-foreground text-background text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              A alíquota efetiva é calculada pela divisão direta entre o Valor Total e a Base de Cálculo, permitindo identificar distorções causadas por benefícios fiscais ou isenções.
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-muted/10 text-muted-foreground">
              <tr>
                <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSort("sigla")}>
                  <div className="flex items-center gap-2">Sigla <ArrowDownUp className="size-3" /></div>
                </th>
                <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] border-r border-foreground/5">Descrição</th>
                <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSort("base")}>
                  <div className="flex items-center justify-end gap-2">Base de Cálculo <ArrowDownUp className="size-3" /></div>
                </th>
                <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary text-amber-500 transition-colors border-r border-foreground/5" onClick={() => toggleSort("aliq")}>
                  <div className="flex items-center justify-end gap-2">Aliq. Efetiva <ArrowDownUp className="size-3" /></div>
                </th>
                <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary text-emerald-500 transition-colors border-r border-foreground/5" onClick={() => toggleSort("valor")}>
                  <div className="flex items-center justify-end gap-2">Valor Total <ArrowDownUp className="size-3" /></div>
                </th>
                <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right">Part. %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/5">
              {tableData.map((t) => (
                <tr key={t.sigla} className="hover:bg-primary/[0.03] transition-colors group">
                  <td className="p-4 border-r border-foreground/5">
                    <span className="font-black text-[10px] px-3 py-1 rounded-lg border uppercase tracking-widest" 
                      style={{ background: `${t.cor}15`, color: t.cor, borderColor: `${t.cor}30` }}>
                      {t.sigla}
                    </span>
                  </td>
                  <td className="p-4 border-r border-foreground/5 text-xs font-bold max-w-[250px] truncate" title={t.desc}>
                    {t.desc}
                  </td>
                  <td className="p-4 border-r border-foreground/5 text-right tabular-nums font-bold">
                    {fmtBRL(t.base)}
                  </td>
                  <td className="p-4 border-r border-foreground/5 text-right tabular-nums font-black text-amber-500">
                    {t.aliq.toFixed(2)}%
                  </td>
                  <td className="p-4 border-r border-foreground/5 text-right tabular-nums font-black text-emerald-500">
                    {fmtBRL(t.valor)}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <div className="w-24 h-1.5 rounded-full bg-muted/20 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000" 
                          style={{ width: `${Math.min(100, t.pct)}%`, backgroundColor: t.cor }} />
                      </div>
                      <span className="tabular-nums text-[10px] font-black w-8">{t.pct.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {totalTributosGeral > 0 && (
              <tfoot className="bg-primary/[0.02]">
                <tr className="border-t border-foreground/5">
                  <td colSpan={2} className="p-4 text-[10px] font-black text-primary uppercase tracking-[0.2em]">Total da Matriz Consolidada</td>
                  <td className="p-4 text-right tabular-nums font-black">{fmtBRL(totalBaseGeral)}</td>
                  <td className="p-4 text-right tabular-nums font-black text-amber-500">{(totalBaseGeral > 0 ? (totalTributosGeral / totalBaseGeral) * 100 : 0).toFixed(2)}%</td>
                  <td className="p-4 text-right tabular-nums font-black text-xl text-emerald-500">{fmtBRL(totalTributosGeral)}</td>
                  <td className="p-4 text-right font-black text-[10px] uppercase">100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function StatBento({ label, value, sub, icon: Icon, color, delay }: any) {
  return (
    <div className={cn("glass-card p-5 group hover:glow-primary animate-in-card")} style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("size-10 rounded-xl flex items-center justify-center transition-colors duration-500", 
          color === 'primary' ? "bg-primary text-white" : 
          color === 'emerald' ? "bg-emerald-500 text-white" :
          color === 'amber' ? "bg-amber-500 text-white" :
          color === 'red' ? "bg-red-500 text-white" :
          "bg-muted/10 text-muted-foreground group-hover:bg-primary group-hover:text-white"
        )}>
          <Icon className="size-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-black tabular-nums tracking-tighter">{value}</div>
      <p className="text-[11px] font-bold text-muted-foreground mt-2">{sub}</p>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-[250px] flex flex-col items-center justify-center text-muted-foreground text-sm gap-4">
      <Info className="size-12 opacity-20" />
      <span className="text-[10px] font-black uppercase tracking-widest">{message}</span>
    </div>
  );
}

function TaxAnalysisSection({ title, subtitle, consolidado, totalReceita, icon: Icon, color }: any) {
  const totalTributos = Object.values(consolidado as Record<string, { valor: number; base: number }>).reduce((s, v) => s + v.valor, 0);
  const totalBase = Object.values(consolidado as Record<string, { valor: number; base: number }>).reduce((s, v) => s + v.base, 0);
  
  const pieData = TRIBUTOS_CONFIG
    .map((t) => ({ name: t.label, value: (consolidado as any)[t.sigla]?.valor ?? 0, cor: t.cor }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  const barData = TRIBUTOS_CONFIG
    .map((t) => ({ name: t.sigla, base: (consolidado as any)[t.sigla]?.base ?? 0, valor: (consolidado as any)[t.sigla]?.valor ?? 0 }))
    .filter((d) => d.valor > 0)
    .sort((a, b) => b.valor - a.valor);

  const aliquotaEfetiva = totalBase > 0 ? (totalTributos / totalBase) * 100 : 0;
  const cargaFiscal = totalReceita > 0 ? (totalTributos / totalReceita) * 100 : 0;

  return (
    <div className="space-y-4 animate-in fade-in duration-700">
      <div className="flex items-center gap-4">
        <div className={cn("size-12 rounded-2xl flex items-center justify-center", 
          color === 'primary' ? "bg-primary/10 text-primary" : "bg-emerald-500/10 text-emerald-500"
        )}>
          <Icon className="size-6" />
        </div>
        <div>
          <h2 className="text-xl font-black tracking-tight">{title}</h2>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mt-1">{subtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatBento label="Total Tributos" value={fmtBRL(totalTributos)} sub="Levantamento Consolidado" icon={Layers} color="primary" />
        <StatBento label="Base de Cálculo" value={fmtBRL(totalBase)} sub="Volume Base" icon={Receipt} color="amber" />
        <StatBento label="Alíquota Efetiva" value={`${aliquotaEfetiva.toFixed(2)}%`} sub="Sobre Base de Cálculo" icon={TrendingUp} color="red" />
        <StatBento label="Carga Fiscal" value={`${cargaFiscal.toFixed(2)}%`} sub="Sobre Receita Bruta" icon={Percent} color="emerald" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-card p-6 hover-glow">
          <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 mb-4 text-muted-foreground">
            <PieIcon className="size-4 text-primary" /> Composição Proporcional
          </h3>

          {pieData.length > 0 ? (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={95} paddingAngle={8} stroke="none">
                    {pieData.map((d, i) => <Cell key={i} fill={d.cor} className="outline-none" />)}
                  </Pie>
                  <Tooltip 
                    formatter={(v: any) => fmtBRL(v)} 
                    contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="Nenhum tributo identificado." />
          )}
        </div>

        <div className="glass-card p-6 hover-glow">
          <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 mb-4 text-muted-foreground">
            <BarChart3 className="size-4 text-primary" /> Matriz Base vs Imposto
          </h3>
          {barData.length > 0 ? (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700 }} tickFormatter={v => `R$${v/1000}k`} />
                  <Tooltip 
                    formatter={(v: any) => fmtBRL(v)}
                    contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Legend verticalAlign="top" iconType="circle" wrapperStyle={{ paddingBottom: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }} />
                  <Bar dataKey="base" name="Base de Cálculo" fill="#1A66CC" radius={[4, 4, 0, 0]} barSize={24} />
                  <Bar dataKey="valor" name="Valor do Imposto" fill={color === 'primary' ? "#FF7A00" : "#10B981"} radius={[4, 4, 0, 0]} barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="Dados insuficientes." />
          )}
        </div>
      </div>
    </div>
  );
}
